import React, { useState, useEffect } from "react";
import { Formik, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useAppDispatch } from "@/redux/store";
import { fetchChangePassword } from "@/redux/profile/settings/changePassword/asyncActions";
import { Modal, Button, PasswordInput } from "@/components";
import { ValidMessages } from "@/resources/locale";
import styles from "./ChangePasswordModal.module.scss";
import SuccessImage from "@/assets/img/success-change-pass.png";

interface ChangePasswordModalProps {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({
  isOpen,
  setIsOpen,
}) => {
  const dispatch = useAppDispatch();
  const [errorMessage, setErrorMessage] = useState("");
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsSuccessModalOpen(false);
      setErrorMessage("");
    }
  }, [isOpen]);

  const initialValues = {
    password: "",
    passwordRepeat: "",
  };

  const validationSchema = Yup.object({
    password: Yup.string()
      .required(ValidMessages.inputs.password.REQUIRED)
      .min(6, ValidMessages.inputs.password.NOT_FULL),
    passwordRepeat: Yup.string()
      .required(ValidMessages.inputs.passwordRepeat.REQUIRED)
      .oneOf([Yup.ref("password"), null], ValidMessages.inputs.passwordRepeat.NOT_MATCH),
  });

  const handleSubmit = async (values: typeof initialValues, { setSubmitting }: any) => {
    try {
      setErrorMessage("");
      await dispatch(
        fetchChangePassword({
          password: values.password,
          passwordRepeat: values.passwordRepeat,
        })
      ).unwrap();

      setIsOpen(false);
      setIsSuccessModalOpen(true);
    } catch (error: any) {
      setErrorMessage(error?.payload || error?.message || "Ошибка при смене пароля");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Modal
        title="Задайте новый пароль"
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        closeModal={() => setIsOpen(false)}
      >
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          validateOnChange={false}
          validateOnBlur={true}
        >
          {(formik) => {
            const isActive = formik.values.password && formik.values.passwordRepeat;
            return (
              <Form className={styles.form}>
                <h3 className={styles.mobileTitle}>Задайте новый пароль</h3>
                <div className={styles.inputsContainer}>
                  <div className={styles.formSection}>
                    <PasswordInput
                      name="password"
                      lable="Пароль"
                      placeholder="********"
                      variant="outlined"
                    />
                    <ErrorMessage
                      component="div"
                      className={styles.errorMessage}
                      name="password"
                    />
                  </div>

                  <div className={styles.formSection}>
                    <PasswordInput
                      name="passwordRepeat"
                      lable="Повторить пароль"
                      placeholder="********"
                      variant="outlined"
                    />
                    <ErrorMessage
                      component="div"
                      className={styles.errorMessage}
                      name="passwordRepeat"
                    />
                  </div>
                </div>

                {errorMessage && (
                  <div className={styles.errorMessage}>{errorMessage}</div>
                )}

                <div className={styles.buttonsContainer}>
                  <Button
                    type="submit"
                    disabled={formik.isSubmitting}
                    newStyles={`${styles.submitButton} ${!isActive ? styles.submitButtonInactive : ''}`}
                  >
                    {formik.isSubmitting ? "Сохранение..." : "Сохранить"}
                  </Button>
                </div>
              </Form>
            );
          }}
        </Formik>
      </Modal>

      <Modal
        title="Данные успешно обновлены!"
        isOpen={isSuccessModalOpen}
        setIsOpen={setIsSuccessModalOpen}
        closeFunc={() => setIsSuccessModalOpen(false)}
        mobileContentSize="fixed"
      >
        <img className={styles.successImage} src={SuccessImage} alt="success" />
      </Modal>
    </>
  );
};
